import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle, Send } from "lucide-react";
import { api } from "@/lib/api";

const EMPTY = { name: "", phone: "", email: "", style: "Blackwork", body_part: "", size: "", budget: "", artist: "", date_pref: "", shift: "Tarde", description: "" };
const STYLES = ["Blackwork", "Fine Line", "Tradicional", "Cobertura", "Outro"];
const SHIFTS = ["Manhã", "Tarde", "Noite"];
const ARTISTS = ["Sem preferência", "Eduardo Camargo"];

const Field = ({ label, children, id }) => (
  <label className="block" htmlFor={id}>
    <span className="block font-mono-dt text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">{label}</span>
    {children}
  </label>
);

export const Booking = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/bookings", form);
      setDone(form);
      setForm(EMPTY);
      toast.success("Pedido de agendamento recebido!", { description: "Entraremos em contato pelo WhatsApp em até 24h." });
    } catch (err) {
      toast.error("Não foi possível enviar. Tente novamente.");
    } finally {
      setSending(false);
    }
  };

  const waText = done
    ? encodeURIComponent(`Olá! Sou ${done.name}. Acabei de pedir um agendamento de ${done.style} (${done.body_part}, ${done.size}) pelo site da Dudu Tattoo.`)
    : "";

  return (
    <section id="agendamento" className="py-24 sm:py-32 bg-[#0d0d10] border-y border-[#27272a]/60" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-5 gap-14">
        <motion.div initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="lg:col-span-2">
          <p className="font-mono-dt text-xs uppercase tracking-[0.35em] text-[#e03131] mb-4">Agendamento</p>
          <h2 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-4xl lg:text-5xl" data-testid="booking-title">
            Sua próxima <span className="text-outline">marca</span>
          </h2>
          <p className="mt-6 text-sm sm:text-base text-zinc-400 leading-relaxed max-w-md">
            Conte sua ideia com o máximo de detalhes. Analisamos a viabilidade, sugerimos o artista ideal
            e retornamos com orçamento e disponibilidade em até 24h.
          </p>
          <div className="mt-8 space-y-3">
            {["Orçamento sem compromisso", "Projeto autoral e exclusivo", "Atendimento via WhatsApp"].map((t) => (
              <p key={t} className="flex items-center gap-3 text-sm text-zinc-300">
                <CheckCircle2 size={15} className="text-[#e03131]" />{t}
              </p>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }} className="lg:col-span-3">
          {done ? (
            <div className="rounded-lg border border-[#e03131]/40 bg-[#121215] p-8 sm:p-10 text-center" data-testid="booking-confirmation">
              <CheckCircle2 size={44} className="text-[#e03131] mx-auto" />
              <h3 className="font-display font-bold text-2xl mt-5">Pedido recebido, {done.name.split(" ")[0]}!</h3>
              <p className="text-sm text-zinc-400 mt-3 max-w-md mx-auto leading-relaxed">
                Seu pedido de <span className="text-[#e2d9cc]">{done.style}</span> foi registrado.
                Quer agilizar? Envie também direto no nosso WhatsApp.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
                <a href={`https://api.whatsapp.com/send?phone=5543984938085&text=${waText}`} target="_blank" rel="noreferrer" data-testid="booking-whatsapp-button"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-[#e03131] text-white font-mono-dt text-xs uppercase tracking-[0.15em] hover:bg-[#c92a2a] transition-colors">
                  <MessageCircle size={15} /> Enviar no WhatsApp
                </a>
                <button onClick={() => setDone(null)} data-testid="booking-new-request-button"
                  className="px-6 py-3.5 rounded-md border border-[#27272a] font-mono-dt text-xs uppercase tracking-[0.15em] text-zinc-300 hover:border-zinc-500 transition-colors">
                  Novo pedido
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-lg border border-[#27272a]/70 bg-[#121215] p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="booking-form">
              <Field label="Nome Completo" id="bk-name"><input id="bk-name" required data-testid="booking-input-name" className="dt-input" placeholder="Seu nome" value={form.name} onChange={set("name")} /></Field>
              <Field label="WhatsApp / Telefone" id="bk-phone"><input id="bk-phone" required data-testid="booking-input-phone" className="dt-input" placeholder="(11) 99999-9999" value={form.phone} onChange={set("phone")} /></Field>
              <Field label="E-mail" id="bk-email"><input id="bk-email" required type="email" data-testid="booking-input-email" className="dt-input" placeholder="voce@email.com" value={form.email} onChange={set("email")} /></Field>
              <Field label="Estilo Desejado" id="bk-style">
                <select id="bk-style" data-testid="booking-select-style" className="dt-input" value={form.style} onChange={set("style")}>
                  {STYLES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Local do Corpo" id="bk-body"><input id="bk-body" data-testid="booking-input-body-part" className="dt-input" placeholder="Ex: Antebraço direito" value={form.body_part} onChange={set("body_part")} /></Field>
              <Field label="Tamanho Aproximado (cm)" id="bk-size"><input id="bk-size" data-testid="booking-input-size" className="dt-input" placeholder="Ex: 15x10 cm" value={form.size} onChange={set("size")} /></Field>
              <Field label="Orçamento Estimado" id="bk-budget"><input id="bk-budget" data-testid="booking-input-budget" className="dt-input" placeholder="Ex: até R$ 1.200" value={form.budget} onChange={set("budget")} /></Field>
              <Field label="Artista Preferido" id="bk-artist">
                <select id="bk-artist" data-testid="booking-select-artist" className="dt-input" value={form.artist} onChange={set("artist")}>
                  {ARTISTS.map((a) => <option key={a}>{a}</option>)}
                </select>
              </Field>
              <Field label="Data de Preferência" id="bk-date"><input id="bk-date" type="date" data-testid="booking-input-date" className="dt-input" value={form.date_pref} onChange={set("date_pref")} /></Field>
              <Field label="Turno" id="bk-shift">
                <select id="bk-shift" data-testid="booking-select-shift" className="dt-input" value={form.shift} onChange={set("shift")}>
                  {SHIFTS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Descrição da Ideia" id="bk-desc">
                  <textarea id="bk-desc" rows={4} data-testid="booking-input-description" className="dt-input resize-none"
                    placeholder="Descreva sua ideia, referências, significado..." value={form.description} onChange={set("description")} />
                </Field>
              </div>
              <button type="submit" disabled={sending} data-testid="booking-form-submit-button"
                className="sm:col-span-2 inline-flex items-center justify-center gap-3 px-7 py-4 rounded-md bg-[#e03131] text-white font-mono-dt text-xs uppercase tracking-[0.2em] hover:bg-[#c92a2a] disabled:opacity-50 transition-colors duration-300">
                {sending ? "Enviando..." : (<>Enviar Pedido de Agendamento <Send size={14} /></>)}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
